export const PET_PROJECT_MIME = 'application/x-ai-pet-project'

function cleanText(value) {
  return String(value || '').trim()
}

export function repoUrlFromFullName(fullName) {
  const value = cleanText(fullName)
  return /^[\w.-]+\/[\w.-]+$/.test(value) ? `https://github.com/${value}` : ''
}

export function isGithubRepoUrl(value) {
  const text = cleanText(value)
  if (!text) return false
  try {
    const parsed = new URL(text)
    const segments = parsed.pathname.split('/').filter(Boolean)
    return parsed.hostname.toLowerCase() === 'github.com'
      && segments.length >= 2
      && /^[\w.-]+$/.test(segments[0])
      && /^[\w.-]+$/.test(segments[1])
  } catch {
    return false
  }
}

export function normalizeProjectFeedPayload(payload = {}) {
  const repoUrl = cleanText(payload.repoUrl)
    || repoUrlFromFullName(payload.fullName || payload.repoFullName)
  const projectTitle = cleanText(
    payload.projectTitle
    || payload.title
    || payload.fullName
    || payload.repoName
    || repoUrl,
  )
  const repoOwner = cleanText(payload.repoOwner)
  const repoName = cleanText(payload.repoName)

  return {
    projectTitle,
    repoName,
    repoOwner,
    repoUrl,
    sourceType: cleanText(payload.sourceType || 'project_card'),
  }
}

export function projectPayloadFromRepo(repo, sourceType = 'daily_repo') {
  const fullName = cleanText(repo?.fullName || repo?.full_name || repo?.name)
  const [owner, name] = fullName.split('/')
  return normalizeProjectFeedPayload({
    projectTitle: fullName || repo?.name,
    repoName: repo?.repoName || repo?.name || name,
    repoOwner: repo?.ownerLogin || repo?.owner_login || owner,
    repoUrl: repo?.htmlUrl || repo?.html_url || repoUrlFromFullName(fullName),
    sourceType,
  })
}

export function projectPayloadFromTool(tool, sourceType = 'open_tool') {
  const fullName = cleanText(tool?.fullName || tool?.name)
  const [owner, name] = fullName.split('/')
  return normalizeProjectFeedPayload({
    projectTitle: tool?.name || fullName,
    repoName: tool?.repoName || name || tool?.name,
    repoOwner: tool?.ownerLogin || owner,
    repoUrl: tool?.htmlUrl || repoUrlFromFullName(fullName),
    sourceType,
  })
}

export function hasFeedableProject(payload) {
  return isGithubRepoUrl(normalizeProjectFeedPayload(payload).repoUrl)
}

export function writeProjectDragData(event, payload) {
  const normalized = normalizeProjectFeedPayload(payload)
  if (!hasFeedableProject(normalized) || !event.dataTransfer) return normalized
  const text = JSON.stringify(normalized)
  event.dataTransfer.effectAllowed = 'copy'
  event.dataTransfer.setData(PET_PROJECT_MIME, text)
  event.dataTransfer.setData('application/json', text)
  event.dataTransfer.setData('text/plain', normalized.repoUrl)
  return normalized
}

export function readProjectDragData(dataTransfer) {
  if (!dataTransfer) return null
  const raw = dataTransfer.getData(PET_PROJECT_MIME)
    || dataTransfer.getData('application/json')
  if (raw) {
    try {
      const parsed = normalizeProjectFeedPayload(JSON.parse(raw))
      return parsed.repoUrl ? parsed : null
    } catch {
      return null
    }
  }
  const text = cleanText(dataTransfer.getData('text/plain'))
  if (!text) return null
  const parsed = normalizeProjectFeedPayload({ repoUrl: text })
  return parsed.repoUrl ? parsed : null
}

export function githubAnalysisUrl(payload, autoAnalyze = true) {
  const normalized = normalizeProjectFeedPayload(payload)
  const params = new URLSearchParams()
  if (normalized.repoUrl) params.set('repo', normalized.repoUrl)
  if (autoAnalyze) params.set('auto', '1')
  const query = params.toString()
  return `/github-analysis${query ? `?${query}` : ''}`
}

export function navigateToPetAnalysis(navigate, payload, options = {}) {
  const normalized = normalizeProjectFeedPayload(payload)
  if (!hasFeedableProject(normalized)) return false
  navigate(githubAnalysisUrl(normalized, options.autoAnalyze !== false), {
    state: {
      autoAnalyze: options.autoAnalyze !== false,
      petProject: normalized,
      repoUrl: normalized.repoUrl,
    },
  })
  return true
}
